import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  Trophy, LayoutDashboard, MessageSquare, Swords, MoreHorizontal,
  Lock, LogOut, ChevronRight, Home,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { hapticTap } from "@/lib/haptics";
import { ENABLED_DASHBOARD_ROUTES, PORTAL_HOME } from "@/lib/webAccess";

const GOLD = "#FFC832";

/**
 * Mobile-only bottom navigation.
 *  - 5 slots, Leaderboard in the middle (slightly larger + elevated).
 *  - "More" opens a bottom sheet with the remaining tabs + logout.
 *  - Locked feature-flag items (e.g. Arena on non-premium plans) show a lock icon
 *    and route the owner to /billing (employees see a toast).
 */
export default function MobileBottomNav({ overflowItems, isFeatureLocked, arenaInbox }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  // Close the More sheet whenever the route changes
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const primary = useMemo(() => ([
    { to: PORTAL_HOME,              icon: Home,            label: "Home",     testid: "bnav-home" },
    { to: "/dashboard/overview",    icon: LayoutDashboard, label: "HQ",       testid: "bnav-headquarters" },
    { to: "/dashboard/chat",        icon: MessageSquare,   label: "The Pit",  testid: "bnav-pit" },
    { to: "/dashboard/leaderboard", icon: Trophy,          label: "Rankings", testid: "bnav-leaderboard", center: true },
    { to: "/dashboard/teams-arena", icon: Swords,          label: "Arena",    testid: "bnav-arena", featureFlag: "arena" },
  ].filter((i) => ENABLED_DASHBOARD_ROUTES.has(i.to))), []);

  const handleLockedClick = () => {
    hapticTap();
    if (user?.role === "owner") {
      navigate("/dashboard/billing");
      toast.message("Upgrade your plan to unlock the Arena");
    } else {
      toast.message("Ask your owner to upgrade the plan to unlock the Arena");
    }
  };

  return (
    <>
      {/* Fixed bottom bar — mobile only */}
      <nav
        className="lg:hidden fixed inset-x-0 bottom-0 z-40"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="mobile-bottom-nav"
      >
        <div
          className="relative flex items-stretch justify-around px-1 pt-2 pb-2 border-t"
          style={{
            background: "linear-gradient(180deg, #0A0B0D 0%, #050607 100%)",
            borderColor: "rgba(255,255,255,0.08)",
            boxShadow: "0 -8px 24px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          <span
            aria-hidden
            className="absolute -top-px inset-x-0 h-px pointer-events-none"
            style={{ background: "linear-gradient(90deg, transparent 10%, rgba(255,200,50,0.35) 50%, transparent 90%)" }}
          />
          {primary.map((item) => (
            <BottomTab
              key={item.to}
              item={item}
              locked={isFeatureLocked(item)}
              onLockedClick={handleLockedClick}
              arenaInbox={arenaInbox}
            />
          ))}

          <button
            type="button"
            onClick={() => { hapticTap(); setMoreOpen(true); }}
            aria-label="More navigation"
            data-testid="bnav-more"
            className={`relative flex flex-col items-center justify-center flex-1 gap-1 py-1.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/40 transition-colors min-w-0 ${
              moreOpen ? "text-[#FFC832]" : "text-white/50 hover:text-white/90"
            }`}
          >
            {moreOpen && <TabPlate />}
            <MoreHorizontal className="w-5 h-5 transition-transform duration-200" style={moreOpen ? { filter: `drop-shadow(0 0 5px ${GOLD}99)` } : undefined} />
            <span className="font-label text-[9px] tracking-[0.04em] uppercase whitespace-nowrap">More</span>
          </button>
        </div>
      </nav>

      {/* Overflow sheet — extends up from the bottom */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="p-0 border-t max-h-[80vh] overflow-y-auto"
          style={{
            background: "linear-gradient(180deg, #0C0D10 0%, #050607 100%)",
            borderColor: "rgba(255,200,50,0.25)",
            boxShadow: "0 -12px 40px rgba(0,0,0,0.7)",
          }}
        >
          <SheetTitle className="sr-only">More navigation</SheetTitle>
          {/* gold hairline along the top edge */}
          <span
            aria-hidden
            className="absolute top-0 inset-x-0 h-px pointer-events-none"
            style={{ background: "linear-gradient(90deg, transparent 5%, rgba(255,200,50,0.5) 50%, transparent 95%)" }}
          />
          <div className="p-4 pb-[calc(env(safe-area-inset-bottom,0px)+16px)]">
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/15" />
            {/* ribbon-style header */}
            <div className="flex items-center gap-3 mb-4">
              <span className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(255,200,50,0.35))" }} />
              <span className="font-label text-[10px] tracking-[0.35em] uppercase" style={{ color: GOLD, textShadow: "0 0 8px rgba(255,200,50,0.4)" }}>
                ★ Command Menu ★
              </span>
              <span className="flex-1 h-px" style={{ background: "linear-gradient(90deg, rgba(255,200,50,0.35), transparent)" }} />
            </div>
            <div className="flex flex-col gap-2" data-testid="bnav-more-list">
              {overflowItems.map((item) => {
                const locked = isFeatureLocked(item);
                const Icon = item.icon;
                const rowClip = { clipPath: "polygon(8px 0, calc(100% - 8px) 0, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0 calc(100% - 8px), 0 8px)" };
                if (locked) {
                  return (
                    <button
                      key={item.to}
                      type="button"
                      onClick={handleLockedClick}
                      data-testid={`bnav-overflow-${item.testid}-locked`}
                      aria-label={`${item.label} — Pro feature. Tap to upgrade.`}
                      className="flex items-center gap-3 px-3 py-2.5 transition-colors"
                      style={{
                        ...rowClip,
                        background: "linear-gradient(180deg, rgba(255,200,50,0.05), rgba(255,200,50,0.015))",
                        border: "1px solid rgba(255,200,50,0.18)",
                        color: "rgba(255,200,50,0.75)",
                      }}
                    >
                      <IconChip Icon={Icon} muted />
                      <span className="font-label text-[11px] tracking-[0.18em] uppercase flex-1 text-left">{item.label}</span>
                      <span
                        data-testid={`bnav-overflow-${item.testid}-pro-ribbon`}
                        className="font-label uppercase leading-none text-[9px] px-1.5"
                        style={{
                          color: "#0A0A0A",
                          background: "linear-gradient(180deg, #FFE07A 0%, #FFC832 55%, #C89614 100%)",
                          border: "1px solid rgba(255,224,122,0.9)",
                          clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%)",
                          boxShadow: "0 0 8px rgba(255,200,50,0.45), inset 0 1px 0 rgba(255,255,255,0.5)",
                          fontWeight: 700,
                          letterSpacing: "0.14em",
                          paddingTop: "3px",
                          paddingBottom: "6px",
                        }}
                      >
                        PRO
                      </span>
                    </button>
                  );
                }
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={`bnav-overflow-${item.testid}`}
                    onClick={() => hapticTap()}
                    className="group"
                  >
                    {({ isActive }) => (
                      <span
                        className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${isActive ? "text-[#FFC832]" : "text-white/80 group-active:text-white"}`}
                        style={{
                          ...rowClip,
                          background: isActive
                            ? "linear-gradient(90deg, rgba(255,200,50,0.14), rgba(255,200,50,0.03))"
                            : "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.015))",
                          border: `1px solid ${isActive ? "rgba(255,200,50,0.45)" : "rgba(255,255,255,0.08)"}`,
                          boxShadow: isActive ? "inset 0 1px 0 rgba(255,200,50,0.2)" : "inset 0 1px 0 rgba(255,255,255,0.04)",
                        }}
                      >
                        <IconChip Icon={Icon} active={isActive} />
                        <span
                          className="font-label text-[11px] tracking-[0.18em] uppercase flex-1 text-left"
                          style={isActive ? { textShadow: "0 0 6px rgba(255,200,50,0.45)" } : undefined}
                        >
                          {item.label}
                        </span>
                        <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform group-active:translate-x-0.5 ${isActive ? "text-[#FFC832]/70" : "text-white/25"}`} />
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>

            <div className="mt-4 pt-4 relative">
              <span
                aria-hidden
                className="absolute top-0 inset-x-6 h-px pointer-events-none"
                style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)" }}
              />
              <button
                type="button"
                onClick={async () => { hapticTap("medium"); await logout(); navigate("/"); toast.success("Logged out"); }}
                data-testid="bnav-logout"
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-[#E13C32]/90 hover:text-[#E13C32] font-label text-[11px] tracking-[0.25em] uppercase transition-colors"
                style={{
                  clipPath: "polygon(8px 0, calc(100% - 8px) 0, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0 calc(100% - 8px), 0 8px)",
                  background: "linear-gradient(180deg, rgba(225,60,50,0.08), rgba(225,60,50,0.02))",
                  border: "1px solid rgba(225,60,50,0.3)",
                }}
              >
                <LogOut className="w-4 h-4" /> Log out
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function IconChip({ Icon, active, muted }) {
  return (
    <span
      className="flex items-center justify-center w-8 h-8 shrink-0"
      style={{
        clipPath: "polygon(5px 0, calc(100% - 5px) 0, 100% 5px, 100% calc(100% - 5px), calc(100% - 5px) 100%, 5px 100%, 0 calc(100% - 5px), 0 5px)",
        background: active
          ? "radial-gradient(circle at 50% 30%, rgba(255,200,50,0.25), rgba(255,200,50,0.05) 75%)"
          : "rgba(255,255,255,0.04)",
        border: `1px solid ${active ? "rgba(255,200,50,0.5)" : "rgba(255,255,255,0.08)"}`,
      }}
    >
      <Icon
        className={`w-4 h-4 ${muted ? "opacity-60" : ""}`}
        style={active ? { color: GOLD, filter: `drop-shadow(0 0 4px ${GOLD}88)` } : undefined}
      />
    </span>
  );
}

function TabPlate() {
  return (
    <span
      aria-hidden
      className="absolute inset-x-1 top-0.5 bottom-0.5 pointer-events-none"
      style={{
        clipPath: "polygon(6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px), 0 6px)",
        background: "linear-gradient(180deg, rgba(255,200,50,0.16), rgba(255,200,50,0.02) 75%)",
        border: "1px solid rgba(255,200,50,0.35)",
        boxShadow: "inset 0 1px 0 rgba(255,200,50,0.25)",
      }}
    />
  );
}

function BottomTab({ item, locked, onLockedClick, arenaInbox }) {
  const Icon = item.icon;
  const center = !!item.center;

  const label = (isActive) => (
    <span
      className="font-label uppercase leading-none whitespace-nowrap text-[9px] tracking-[0.04em]"
      style={isActive ? { textShadow: `0 0 6px rgba(255,200,50,0.5)` } : undefined}
    >
      {item.label}
    </span>
  );

  if (locked) {
    return (
      <button
        type="button"
        onClick={onLockedClick}
        data-testid={`${item.testid}-locked`}
        aria-label={`${item.label} — Pro feature. Tap to upgrade.`}
        className="relative flex flex-col items-center justify-center flex-1 gap-1 py-1.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#FFC832]/50 transition-colors min-w-0"
        style={{ color: "rgba(255,200,50,0.72)" }}
      >
        <Icon
          className="w-5 h-5 transition-transform duration-200 group-active:scale-90"
          style={{ filter: `drop-shadow(0 0 5px ${GOLD}66)` }}
        />
        {label(false)}
        {/* Premium "PRO" ribbon — replaces the plain red lock */}
        <span
          aria-hidden
          data-testid={`${item.testid}-pro-ribbon`}
          className="absolute -top-1 left-1/2 -translate-x-1/2 font-label uppercase tracking-[0.14em] leading-none px-1.5 py-[3px] text-[8px]"
          style={{
            color: "#0A0A0A",
            background: "linear-gradient(180deg, #FFE07A 0%, #FFC832 55%, #C89614 100%)",
            border: "1px solid rgba(255,224,122,0.9)",
            clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%)",
            boxShadow: "0 0 10px rgba(255,200,50,0.55), inset 0 1px 0 rgba(255,255,255,0.55)",
            fontWeight: 700,
            letterSpacing: "0.14em",
          }}
        >
          PRO
        </span>
      </button>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={item.testid}
      onClick={() => hapticTap()}
      className={({ isActive }) => `group relative flex flex-col items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/40 transition-colors min-w-0 ${center ? "justify-end flex-[1.5] pt-1.5 pb-2" : "justify-center flex-1 py-1.5"} ${
        isActive ? "text-[#FFC832]" : "text-white/50 hover:text-white/90"
      }`}
    >
      {({ isActive }) => (
        <>
          {isActive && !center && <TabPlate />}
          {center ? (
            <span className="relative flex items-center justify-center -mt-6 mb-0.5 shrink-0" style={{ width: 58, height: 58 }}>
              {/* outer ring */}
              <span
                aria-hidden
                className="absolute inset-0 rounded-full transition-all duration-300"
                style={{
                  border: `1px solid ${isActive ? "rgba(255,200,50,0.30)" : "rgba(255,255,255,0.07)"}`,
                  background: "#050607",
                  boxShadow: isActive ? "0 0 22px rgba(255,200,50,0.28)" : "0 6px 16px rgba(0,0,0,0.6)",
                }}
              />
              {/* inner disc */}
              <span
                className="relative flex items-center justify-center rounded-full transition-all duration-300"
                style={{
                  width: 48,
                  height: 48,
                  background: isActive
                    ? `radial-gradient(circle at 50% 28%, rgba(255,200,50,0.40), rgba(255,200,50,0.06) 68%), #0C0D10`
                    : "linear-gradient(180deg, #17191E, #0A0B0D)",
                  border: `1px solid ${isActive ? "rgba(255,200,50,0.65)" : "rgba(255,255,255,0.12)"}`,
                  boxShadow: isActive
                    ? `0 0 16px rgba(255,200,50,0.35), inset 0 0 12px rgba(255,200,50,0.18), inset 0 1px 0 rgba(255,255,255,0.15)`
                    : "inset 0 1px 0 rgba(255,255,255,0.08)",
                }}
              >
                <Icon
                  className="w-6 h-6 transition-transform duration-200 group-active:scale-90"
                  style={isActive ? { color: GOLD, filter: `drop-shadow(0 0 6px ${GOLD}aa)` } : undefined}
                />
              </span>
            </span>
          ) : (
            <Icon
              className={`w-5 h-5 transition-all duration-200 group-active:scale-90 ${isActive ? "-translate-y-px" : ""}`}
              style={isActive ? { filter: `drop-shadow(0 0 5px ${GOLD}99)` } : undefined}
            />
          )}
          {label(isActive)}
          {item.to === "/dashboard/teams-arena" && arenaInbox?.pending_for_me > 0 && (
            <span
              className="absolute top-0.5 right-3 w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: "#E13C32", boxShadow: "0 0 6px rgba(225,60,50,0.7)" }}
              data-testid="bnav-arena-badge"
            />
          )}
        </>
      )}
    </NavLink>
  );
}

